import { GetStaticProps } from 'next';
import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { withTheme } from '@emotion/react';
import { Theme } from '@theme/base';
import { withGlobalProvider } from 'providers/globalProvider';
import { PageProps } from '@model/pages';
import { TripTypes } from '@model/common';
import { Revalidate } from '@model/config/revalidate';
import { AppVariant } from '@model/config/brands';
import { PageTemplates, PagePaths, PageConfig } from '@model/content';
import { ContentApi } from '@model/iceberg/service/content';
import { storeWrapper } from '@state/store';
import { initializeApp } from '@state/initializeApp';
import { NavBarContainer } from '@components/navigation';
import { ComponentsContainer } from '@components/content';
import { Footer } from '@components/footer';
import { getHomeMeta } from '@components/meta/get-home-meta';
import { getHTML } from '@util/common';
import { isMobile } from '@styles/breakpoints';
import { fetchComponentsData } from '@util/components';

export interface HomePageProps extends PageProps {
  theme: Theme;
  pageConfig: PageConfig;
  componentsData: any;
  path: PagePaths;
}

const HomePage = ({ pageConfig, componentsData, path, promoBannerTripType }: HomePageProps) => {
  const router = useRouter();

  useEffect(() => {
    router.prefetch('/search');
  }, []);
  const isWhiteLabel = AppVariant.isWhitelabel();

  const { components } = pageConfig;

  return (
    <>
      {!isWhiteLabel && (
        <Script type="application/ld+json" dangerouslySetInnerHTML={getHTML(JSON.stringify(getHomeMeta()))} />
      )}
      <NavBarContainer isOverlay={!isMobile()} promoBannerTripType={promoBannerTripType} />
      <ComponentsContainer
        components={components}
        componentsData={componentsData}
        path={path}
        isWhiteLabel={isWhiteLabel}
        tripType={TripTypes.HOLIDAYS}
      />
      <Footer hideReviews />
    </>
  );
};

export const getStaticProps: GetStaticProps = storeWrapper.getStaticProps((store) => async () => {
  const props = await initializeApp(store, async () => {
    const path = PagePaths.HOMEPAGE;
    const pageConfig = await new ContentApi().getPageConfig({
      template: PageTemplates.HOMEPAGE,
      path
    });

    const componentsData = await fetchComponentsData({
      path,
      components: pageConfig?.components
    });

    return {
      pageConfig,
      path: PagePaths.HOMEPAGE,
      componentsData,
      promoBannerTripType: TripTypes.ALL
    };
  });
  return {
    ...props,
    revalidate: Revalidate.HALF_HOUR
  };
});

export default withGlobalProvider(withTheme(HomePage));
